import { useEffect, useState, type ReactNode } from 'react';
import { get, put } from '@aiptyg/api-client';
import { useMessageReviewConfig } from '../../features/settings';
import type { MessageReviewReasoningEffort } from '../../features/settings/types/settings.types';
import { formatDateTime } from '../../lib/format';

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-400';
const selectCls =
  'h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-400';

const reasoningLabels: Record<MessageReviewReasoningEffort, string> = {
  default: 'default（省略 reasoning）',
  low: 'low',
  medium: 'medium',
  high: 'high',
  xhigh: 'xhigh',
  max: 'max',
  ultra: 'ultra',
};

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
      {hint ? <span className="mt-1 block text-xs leading-5 text-gray-400">{hint}</span> : null}
    </label>
  );
}

function StatusBadge({ ok, text }: { ok: boolean; text: string }) {
  return (
    <span
      className={`inline-flex h-7 items-center rounded-md px-2.5 text-xs font-medium ${
        ok ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {text}
    </span>
  );
}

function displayDate(value?: string) {
  return value ? formatDateTime(value) : '-';
}

export function MessageReviewPage() {
  const [keyRevision, setKeyRevision] = useState(0);
  return <div className="max-w-6xl space-y-6"><MessageTaskKeys onSaved={() => setKeyRevision(value => value + 1)} /><MessageReviewSettings key={keyRevision} /></div>;
}

function MessageReviewSettings() {
  const {
    data,
    form,
    loading,
    saving,
    testing,
    error,
    modelOptions,
    reasoningOptions,
    testResult,
    updateField,
    save,
    testConnection,
    reload,
  } = useMessageReviewConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  const selectedCapability = data?.capabilities?.[form.model];
  const worker = data?.worker;
  const workerReady = data?.worker_ready ?? worker?.worker_ready ?? worker?.ready ?? Boolean(data?.configured && data?.enabled);
  const workerStatus = data?.worker_status ?? worker?.status ?? (workerReady ? 'ready' : 'not_ready');
  const lastSuccessAt = data?.last_success_at ?? worker?.last_success_at;
  const lastFailureAt = data?.last_failure_at ?? worker?.last_failure_at;
  const lastErrorCode = data?.last_error_code ?? worker?.last_error_code;
  const queueDepth = data?.queue_depth ?? worker?.queue_depth;
  const runningJobs = data?.running_jobs ?? worker?.running_jobs;

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-2">
          <p className="text-sm leading-6 text-gray-500">
            配置短信代发截图审核使用的识别引擎。PaddleOCR 使用内网服务地址，OpenAI 作为备用方案保留。
          </p>
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs leading-5 text-amber-700">
            API Key 保存后仅显示配置状态和末四位；测试连接只展示稳定 code 与 request ID。
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge ok={Boolean(data?.configured)} text={data?.configured ? '当前引擎已配置' : '当前引擎未配置'} />
          {form.provider === 'openai' ? (
            <StatusBadge ok={Boolean(data?.api_key_configured)} text={data?.api_key_configured ? `Key 已配置 ${data.api_key_last4 ? `****${data.api_key_last4}` : ''}` : 'Key 未配置'} />
          ) : null}
          <StatusBadge ok={workerReady} text={`Worker ${workerStatus}`} />
        </div>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="space-y-5">
            <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(event) => updateField('enabled', event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              启用自动截图审核
            </label>

            <div className="grid gap-4 md:grid-cols-2">
              <Field label="当前引擎">
                <select
                  value={form.provider}
                  onChange={(event) => updateField('provider', event.target.value as typeof form.provider)}
                  className={selectCls}
                >
                  <option value="local_screenshot">PaddleOCR + 气泡颜色</option>
                  <option value="openai">OpenAI 视觉审核（备用）</option>
                </select>
              </Field>

              <Field label="模型" hint="来自 GET capability map 的 allowed_models。">
                <select
                  value={form.model}
                  onChange={(event) => updateField('model', event.target.value)}
                  className={selectCls}
                >
                  {modelOptions.length === 0 ? <option value="">暂无可用模型</option> : null}
                  {modelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Reasoning" hint={form.provider === 'openai' ? 'default 表示请求中省略 reasoning 字段。' : '本地识别不使用 reasoning。'}>
                <select
                  value={form.reasoning_effort}
                  onChange={(event) => updateField('reasoning_effort', event.target.value as MessageReviewReasoningEffort)}
                  className={selectCls}
                >
                  {reasoningOptions.map((effort) => (
                    <option key={effort} value={effort}>
                      {reasoningLabels[effort] ?? effort}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            {form.provider === 'local_screenshot' ? (
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="PaddleOCR 服务地址" hint="生产环境可填写独立机器的内网 HTTP 地址。">
                  <input
                    type="url"
                    value={form.local_ocr_base_url}
                    onChange={(event) => updateField('local_ocr_base_url', event.target.value)}
                    className={inputCls}
                    placeholder="http://10.0.0.10:19090"
                  />
                </Field>
                <Field label="每个工作进程的 CPU 线程数" hint="默认 3，范围 1～32；保存后在下一次审核请求生效。">
                  <input
                    type="number"
                    min={1}
                    max={32}
                    step={1}
                    value={form.local_ocr_cpu_threads}
                    onChange={(event) => updateField('local_ocr_cpu_threads', Number(event.target.value))}
                    className={inputCls}
                  />
                </Field>
                <p className="text-xs text-gray-500 md:col-span-2">工作进程数量由 OCR 服务部署配置决定，每个进程逐张识别。如需调整进程数量，请更新部署配置并重启 OCR 服务。</p>
              </div>
            ) : null}

            <Field
              label="OpenAI API Key"
              hint={data?.api_key_configured ? '留空保存会保留旧 Key；输入新 Key 才会轮换。' : '首次启用前需要保存 API Key。'}
            >
              <input
                type="password"
                value={form.api_key}
                onChange={(event) => updateField('api_key', event.target.value)}
                className={inputCls}
                disabled={form.provider !== 'openai'}
                autoComplete="new-password"
                spellCheck={false}
                placeholder={form.provider === 'openai' ? data?.api_key_configured ? '已配置，留空表示不修改' : '请输入 OpenAI API Key' : '当前使用本地截图审核'}
              />
            </Field>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field label="Timeout（秒）">
                <input
                  type="number"
                  min={5}
                  max={300}
                  value={form.timeout_seconds}
                  onChange={(event) => updateField('timeout_seconds', Number(event.target.value) || 0)}
                  className={inputCls}
                />
              </Field>
              <Field label="重试次数">
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={form.max_retries}
                  onChange={(event) => updateField('max_retries', Number(event.target.value) || 0)}
                  className={inputCls}
                />
              </Field>
              <Field label="置信度阈值">
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={form.confidence_threshold}
                  onChange={(event) => updateField('confidence_threshold', Number(event.target.value))}
                  className={inputCls}
                />
              </Field>
              <Field label="人工抽样比例">
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.01}
                  value={form.manual_review_ratio}
                  onChange={(event) => updateField('manual_review_ratio', Number(event.target.value))}
                  className={inputCls}
                />
              </Field>
            </div>
          </div>

          <aside className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h2 className="text-sm font-semibold text-gray-900">当前能力</h2>
            <dl className="space-y-2 text-xs">
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">图片输入</dt>
                <dd className="font-medium text-gray-900">{selectedCapability?.supports_images ? '支持' : '不支持'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Strict schema</dt>
                <dd className="font-medium text-gray-900">{selectedCapability?.supports_strict_json_schema ? '支持' : '不支持'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Reasoning</dt>
                <dd className="font-medium text-gray-900">{selectedCapability?.supports_reasoning ? '支持' : '不支持'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Base URL</dt>
                <dd className="max-w-[180px] truncate font-medium text-gray-900">{data?.base_url || '-'}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">环境兜底</dt>
                <dd className="font-medium text-gray-900">{data?.using_env_fallback ? '使用中' : '未使用'}</dd>
              </div>
            </dl>
          </aside>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={save}
            disabled={saving || modelOptions.length === 0}
            className="inline-flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : null}
            保存配置
          </button>
          <button
            type="button"
            onClick={() => void testConnection()}
            disabled={testing || saving || (form.provider === 'openai' && !data?.api_key_configured)}
            className="inline-flex h-10 items-center rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {testing ? '测试中...' : '测试连接'}
          </button>
          {testResult ? (
            <span className={`text-sm ${testResult.success ? 'text-emerald-700' : 'text-red-600'}`}>
              code: {testResult.code || '-'}{testResult.request_id ? ` · request ID: ${testResult.request_id}` : ''}
            </span>
          ) : null}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs text-gray-500">最近成功</div>
          <div className="mt-2 text-sm font-medium text-gray-900">{displayDate(lastSuccessAt)}</div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs text-gray-500">最近失败</div>
          <div className="mt-2 text-sm font-medium text-gray-900">{displayDate(lastFailureAt)}</div>
          {lastErrorCode ? <div className="mt-1 break-words text-xs text-red-500">code: {lastErrorCode}</div> : null}
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="text-xs text-gray-500">队列状态</div>
          <div className="mt-2 text-sm font-medium text-gray-900">
            等待 {queueDepth ?? '-'} · 运行 {runningJobs ?? '-'}
          </div>
        </div>
      </section>

      {data?.updated_at ? (
        <div className="text-xs text-gray-400">
          最后更新：{formatDateTime(data.updated_at)}
          {data.updated_by != null ? ` · 操作人 ID：${data.updated_by}` : ''}
        </div>
      ) : null}
    </div>
  );
}

function MessageTaskKeys({ onSaved }: { onSaved: () => void }) {
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ data_encryption_key: '', hmac_key: '', openai_config_encryption_key: '' });
  const endpoint = '/v1/admin/settings/message-review/keys';
  const load = async () => {
    setError('');
    try { const result = await get<{ configured: boolean }>(endpoint); setConfigured(result.configured); }
    catch { setError('读取密钥配置失败，请重试'); }
  };
  useEffect(() => { void load(); }, []);
  const saveKeys = async () => {
    if (new TextEncoder().encode(form.data_encryption_key).length !== 32 || !form.hmac_key || !form.openai_config_encryption_key) {
      setError('数据加密密钥必须为32字节，其余两项不能为空'); return;
    }
    setSaving(true); setError('');
    try {
      const result = await put<{ configured: boolean }>(endpoint, form);
      setConfigured(result.configured);
      setForm({ data_encryption_key: '', hmac_key: '', openai_config_encryption_key: '' });
      onSaved();
    } catch (err) { setError(err instanceof Error ? err.message : '保存密钥失败'); }
    finally { setSaving(false); }
  };
  return <section className="space-y-4 rounded-xl border border-gray-200 bg-white p-5">
    <h2 className="text-base font-semibold">短信任务密钥</h2>
    <p className="text-sm text-gray-500">密钥由后台统一保存，保存后立即生效，无需重启服务。已有号码池或审核配置时，请填入原密钥；保存后不回显、不允许直接更换。</p>
    {configured === true ? <StatusBadge ok text="密钥已配置" /> : configured === false ? <>
      <div className="grid gap-4 md:grid-cols-3">
        {([
          ['data_encryption_key', '号码池数据加密密钥', '必须为32字节，用于加密号码和正文'],
          ['hmac_key', 'HMAC 校验密钥', '不能为空，用于去重和数据校验'],
          ['openai_config_encryption_key', '审核配置加密密钥', '不能为空，用于加密审核 API Key'],
        ] as const).map(([key, label, hint]) => <Field key={key} label={label} hint={hint}>
          <input type="password" autoComplete="new-password" className={inputCls} value={form[key]} disabled={saving} onChange={event => setForm(previous => ({ ...previous, [key]: event.target.value }))} />
        </Field>)}
      </div>
      <button type="button" disabled={saving} onClick={() => void saveKeys()} className="rounded-md bg-primary px-4 py-2 text-sm text-white disabled:opacity-50">{saving ? '保存中…' : '保存密钥'}</button>
    </> : <span className="text-sm text-gray-500">正在读取配置…</span>}
    {error && <div className="text-sm text-red-500">{error} <button type="button" onClick={() => void load()} className="underline">重新读取</button></div>}
  </section>;
}
